import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, inr } from "@/lib/api";
import { estimateTotal } from "@/lib/estimate";
import { useAuth } from "@/context/AuthContext";
import Stepper from "@/components/estimator/Stepper";
import UploadStep from "@/components/estimator/UploadStep";
import RoomsStep from "@/components/estimator/RoomsStep";
import PackageStep from "@/components/estimator/PackageStep";
import DetailsStep from "@/components/estimator/DetailsStep";

export default function Estimate() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [step, setStep] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [source, setSource] = useState("manual");
  const [aiInfo, setAiInfo] = useState(null);
  const [tier, setTier] = useState(params.get("tier") || "premium");
  const [addons, setAddons] = useState([]);
  const [client, setClient] = useState({ name: "", email: "", phone: "", city: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)); }, []);
  useEffect(() => { if (user) setClient((c) => ({ ...c, name: c.name || user.name, email: c.email || user.email })); }, [user]);

  const live = useMemo(() => estimateTotal(pricing, rooms, tier, addons), [pricing, rooms, tier, addons]);

  const onRooms = (r, src, info) => { setRooms(r); setSource(src); setAiInfo(info || null); setStep(1); };

  const next = async () => {
    if (step === 1 && !rooms.some((r) => r.included)) return toast.error("Include at least one room");
    if (step < 3) return setStep(step + 1);
    if (!client.name || !client.email) return toast.error("Please enter your name and email");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) return toast.error("Please enter a valid email address");
    setSubmitting(true);
    try {
      const payload = { client, tier, addons, source, rooms: rooms.map((r) => ({ ...r, area_sqft: Number(r.area_sqft) || 1 })) };
      const { data } = await api.post("/quotes", payload);
      nav(`/quote/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e));
      setSubmitting(false);
    }
  };

  if (!pricing) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="estimate-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-brand-accent inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Instant Quotation</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-brand-ink mt-3">Let's price your dream home.</h1>
        </div>
        <Stepper step={step} />
      </div>

      {step === 0 && <UploadStep onRooms={onRooms} />}
      {step === 1 && <RoomsStep rooms={rooms} setRooms={setRooms} roomTypes={pricing.room_types} aiInfo={aiInfo} />}
      {step === 2 && <PackageStep pricing={pricing} tier={tier} setTier={setTier} addons={addons} setAddons={setAddons} />}
      {step === 3 && <DetailsStep client={client} setClient={setClient} user={user} />}

      {step > 0 && (
        <div className="sticky bottom-4 mt-10 rounded-2xl bg-brand-ink text-brand-cream px-6 py-4 flex items-center justify-between gap-4 shadow-2xl" data-testid="wizard-footer">
          <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-sm text-brand-line hover:text-white" data-testid="wizard-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-muted-light">Live estimate · incl. GST</p>
            <p className="font-serif-display text-2xl md:text-3xl" data-testid="live-estimate-total">{inr(live)}</p>
          </div>
          <button onClick={next} disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-brand-accent hover:bg-brand-accent-dark px-6 py-3 text-sm transition-colors disabled:opacity-60" data-testid="wizard-next-btn">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Crafting quote…</> : step === 3 ? <>Generate Quotation <ArrowRight className="w-4 h-4" /></> : <>Continue <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      )}
    </section>
  );
}
